import React, { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setDialog } from 'src/features/dialog/dialogSlice';
import {
  useGetTenantInformationQuery,
  useSendTenantTestMailMutation,
  useUpdateTenantMailSettingsMutation,
} from 'src/features/tenants/tenantsApiSlice';
import { selectCurrentUserData } from 'src/features/auth/authSlice';
import MailTemplatePageLayout from './MailTemplatePageLayout';

const tokens = [
  {
    value: '{{formalGreeting}}',
    label: 'Formale Anrede',
    desc: 'Automatische Anrede basierend auf dem Titel: Herr → Sehr geehrter Herr [Nachname], Frau → Sehr geehrte Frau [Nachname], kein Titel → Guten Tag [Vollständiger Name].',
  },
  {
    value: '{{memberFirstName}}',
    label: 'Vorname',
    desc: 'Vorname des Mitglieds.',
  },
  {
    value: '{{memberLastName}}',
    label: 'Nachname',
    desc: 'Nachname des Mitglieds.',
  },
  {
    value: '{{memberFullName}}',
    label: 'Vollständiger Name',
    desc: 'Vor- und Nachname des Mitglieds.',
  },
  {
    value: '{{contractOwner}}',
    label: 'Vertragsinhaber',
    desc: 'Name der Person, die den Vertrag abgeschlossen hat.',
  },
  {
    value: '{{contractEmail}}',
    label: 'E-Mail-Adresse',
    desc: 'Die im Vertrag hinterlegte E-Mail-Adresse.',
  },
  {
    value: '{{tenantName}}',
    label: 'Vereinsname',
    desc: 'Name Ihres Vereins.',
  },
  {
    value: '{{today}}',
    label: 'Heutiges Datum',
    desc: 'Das aktuelle Datum zum Zeitpunkt des E-Mail-Versands.',
  },
];

const MailSelfRegReceiptTemplateSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const currentUserData = useSelector(selectCurrentUserData);
  const { data: tenantInfo, isLoading } = useGetTenantInformationQuery();
  const [updateTenantMailSettings, { isLoading: isSaving }] =
    useUpdateTenantMailSettingsMutation();
  const [sendTenantTestMail, { isLoading: isTesting }] =
    useSendTenantTestMailMutation();

  const defaultValues = useMemo(
    () => ({
      selfRegReceiptSubject:
        tenantInfo?.settings?.mail?.selfRegReceiptSubject ||
        'Eingangsbestätigung Ihrer Anmeldung',
      selfRegReceiptTemplate:
        tenantInfo?.settings?.mail?.selfRegReceiptTemplate ||
        '{{formalGreeting}},\n\nvielen Dank für Ihre Anmeldung bei {{tenantName}}.\n\nIhre Anmeldung wurde erfolgreich eingereicht und wird nun von unserem Team geprüft. Sie erhalten in Kürze eine E-Mail mit der Bestätigung oder ggf. Rückfragen zu Ihrer Anmeldung.\n\nSobald Ihre Anmeldung genehmigt wurde, erhalten Sie eine weitere E-Mail mit der Anmeldebestätigung als PDF-Dokument.\n\nAnbei finden Sie eine Zusammenfassung Ihrer übermittelten Daten:\n\n',
      selfRegReceiptTemplateMode:
        tenantInfo?.settings?.mail?.selfRegReceiptTemplateMode || 'auto',
      selfRegReceiptBcc: tenantInfo?.settings?.mail?.selfRegReceiptBcc || '',
      testRecipient: currentUserData?.mail || '',
    }),
    [tenantInfo, currentUserData]
  );

  const { register, handleSubmit, control, reset, setValue, getValues } =
    useForm({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleInsertToken = (token) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertText', false, token);
      const next = editorRef.current.innerHTML || '';
      setValue('selfRegReceiptTemplate', next, { shouldDirty: true });
      return;
    }
    const current = getValues('selfRegReceiptTemplate') || '';
    setValue(
      'selfRegReceiptTemplate',
      current ? `${current} ${token}` : token,
      { shouldDirty: true }
    );
  };

  const onSubmit = async (data) => {
    const { testRecipient, ...mailSettings } = data;
    try {
      const result = await updateTenantMailSettings(mailSettings).unwrap();
      if (result?.type === 'success') {
        dispatch(
          setDialog({
            title: 'Einstellungen gespeichert',
            type: 'SUCCESS',
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: 'Fehler',
          type: 'ERROR',
          data: {
            errorText:
              error?.data?.message ||
              'Einstellungen konnten nicht gespeichert werden.',
          },
        })
      );
    }
  };

  const handleSendTest = async () => {
    const recipient = (getValues('testRecipient') || '').trim();
    if (!recipient) {
      dispatch(
        setDialog({
          title: 'Hinweis',
          type: 'ERROR',
          data: { errorText: 'Bitte eine Test-E-Mail-Adresse angeben.' },
        })
      );
      return;
    }
    try {
      const result = await sendTenantTestMail({ recipient }).unwrap();
      if (result?.type === 'success') {
        dispatch(
          setDialog({
            title: 'Test-E-Mail gesendet',
            type: 'SUCCESS',
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: 'Fehler',
          type: 'ERROR',
          data: {
            errorText:
              error?.data?.message || 'Test-E-Mail konnte nicht gesendet werden.',
          },
        })
      );
    }
  };

  if (isLoading) return null;

  return (
    <MailTemplatePageLayout
      title='Selbstanmeldung — Eingangsbestätigung'
      description='Individualisieren Sie den Text der Eingangsbestätigung, die unmittelbar nach einer Selbstanmeldung automatisch an das Mitglied versendet wird.'
      subjectFieldName='selfRegReceiptSubject'
      templateFieldName='selfRegReceiptTemplate'
      bccFieldName='selfRegReceiptBcc'
      templateType='selfRegReceipt'
      modeFieldName='selfRegReceiptTemplateMode'
      currentHtmlTemplate={tenantInfo?.settings?.mail?.selfRegReceiptHtmlTemplate}
      tokens={tokens}
      control={control}
      register={register}
      handleInsertToken={handleInsertToken}
      editorRef={editorRef}
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      handleSendTest={handleSendTest}
      isSaving={isSaving}
      isTesting={isTesting}
      tenantName={tenantInfo?.tenantName}
      navigate={navigate}
    />
  );
};

export default MailSelfRegReceiptTemplateSettings;
